import React, { useMemo, useState } from "react";
import { RULER_HEIGHT_PX } from "./Ruler";

export type RulerSemanticMarkerView = {
  id: string;
  timeSec: number;
  kind: "event" | "beat";
  label?: string;
  emphasis?: "low" | "normal" | "high";
  accentColor?: string;
  note?: string | null;
  origin?: string | null;
  frameIndex?: number | null;
};

const RULER_MARKER_TICKER_STYLE_ID = "ruler-semantic-marker-ticker-keyframes";

export interface RulerSemanticOverlayProps {
  markers: RulerSemanticMarkerView[];
  viewportStartSec: number;
  viewportEndSec: number;
  viewportWidthPx: number;
  pxPerSec: number;
  showEvents: boolean;
  showBeats: boolean;
  activeMarkerId?: string | null;
}

type RulerSemanticVisibilityOptions = {
  viewportStartSec: number;
  viewportEndSec: number;
  showEvents: boolean;
  showBeats: boolean;
  activeMarkerId?: string | null;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function normalizeHexColor(raw: unknown): string {
  const value = String(raw ?? "").trim();
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : "";
}

function hexToRgba(hex: string, alpha: number): string {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return `rgba(196,181,253,${alpha})`;
  const red = parseInt(normalized.slice(1, 3), 16);
  const green = parseInt(normalized.slice(3, 5), 16);
  const blue = parseInt(normalized.slice(5, 7), 16);
  return `rgba(${red},${green},${blue},${alpha})`;
}

function ensureRulerMarkerTickerStyle() {
  if (typeof document === "undefined") return;
  if (document.getElementById(RULER_MARKER_TICKER_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = RULER_MARKER_TICKER_STYLE_ID;
  style.textContent = `
    @keyframes ruler-semantic-marker-ticker {
      0% { transform: translate3d(0, 0, 0); }
      100% { transform: translate3d(-50%, 0, 0); }
    }
  `;
  document.head.appendChild(style);
}

function formatMarkerOriginLabel(origin: string | null | undefined): string {
  const normalized = String(origin ?? "").trim().toLowerCase();
  switch (normalized) {
    case "transport_panel":
      return "Panel marker";
    case "annotation_surface":
      return "Annotation marker";
    case "command_palette":
      return "Palette marker";
    case "xr_bridge":
      return "XR marker";
    default:
      return normalized ? normalized.replace(/[_-]+/g, " ") : "Marker";
  }
}

export function isRulerSemanticMarkerVisible(
  marker: RulerSemanticMarkerView,
  {
    viewportStartSec,
    viewportEndSec,
    showEvents,
    showBeats,
    activeMarkerId,
  }: RulerSemanticVisibilityOptions,
): boolean {
  const markerId = String(marker.id ?? "").trim();
  const activeMarkerIdStr = String(activeMarkerId ?? "").trim();
  const isActive = !!markerId && markerId === activeMarkerIdStr;

  if (!Number.isFinite(marker.timeSec)) return false;

  // Product rule: active marker remains represented even if the ruler viewport
  // is temporarily out of sync with the main timeline viewport.
  if (!isActive && (marker.timeSec < viewportStartSec || marker.timeSec > viewportEndSec)) return false;

  // Product rule: active marker survives category toggle filtering.
  if (!isActive) {
    if (marker.kind === "event" && !showEvents) return false;
    if (marker.kind === "beat" && !showBeats) return false;
  }

  return true;
}

export default function RulerSemanticOverlay({
  markers,
  viewportStartSec,
  viewportEndSec,
  viewportWidthPx,
  pxPerSec,
  showEvents,
  showBeats,
  activeMarkerId,
}: RulerSemanticOverlayProps): JSX.Element {
  const activeMarkerIdStr = String(activeMarkerId ?? "").trim();
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);

  ensureRulerMarkerTickerStyle();

  const visibleMarkers = useMemo(() => {
    return (markers ?? [])
      .filter((marker) =>
        isRulerSemanticMarkerVisible(marker, {
          viewportStartSec,
          viewportEndSec,
          showEvents,
          showBeats,
          activeMarkerId: activeMarkerIdStr,
        }),
      )
      .slice()
      .sort((a, b) => a.timeSec - b.timeSec);
  }, [markers, viewportStartSec, viewportEndSec, showEvents, showBeats, activeMarkerIdStr]);

  const eventLabelVisibility = useMemo(() => {
    const activeId = activeMarkerIdStr;
    const eventMarkers = visibleMarkers
      .filter((marker) => marker.kind === "event")
      .map((marker) => ({
        id: String(marker.id ?? ""),
        x: Math.round((marker.timeSec - viewportStartSec) * pxPerSec) + 0.5,
      }))
      .filter((entry) => entry.id && Number.isFinite(entry.x));

    const minGapPx = 54;
    const visible = new Set<string>();

    if (activeId) {
      const active = eventMarkers.find((entry) => entry.id === activeId);
      if (active) visible.add(active.id);
    }

    let lastShownX = -Infinity;
    for (const entry of eventMarkers) {
      if (visible.has(entry.id)) {
        lastShownX = entry.x;
        continue;
      }

      if (entry.x - lastShownX >= minGapPx) {
        visible.add(entry.id);
        lastShownX = entry.x;
      }
    }

    return visible;
  }, [activeMarkerIdStr, pxPerSec, viewportStartSec, visibleMarkers]);

  return (
    <div
      className="pointer-events-none absolute inset-0 z-[2]"
      aria-hidden="true"
      style={{ height: RULER_HEIGHT_PX, width: viewportWidthPx }}
    >
      {visibleMarkers.map((marker) => {
        const markerId = String(marker.id ?? "");
        const isActive = !!markerId && markerId === activeMarkerIdStr;
        const isHovered = !!markerId && markerId === hoveredMarkerId;
        const rawX = (marker.timeSec - viewportStartSec) * pxPerSec;
        const snappedX = Math.round(rawX) + 0.5;
        if (!isActive && (snappedX < -24 || snappedX > viewportWidthPx + 24)) return null;

        const x = isActive
          ? clamp(snappedX, 0.5, Math.max(0.5, viewportWidthPx - 0.5))
          : snappedX;

        if (marker.kind === "beat") {
          const resolvedBeatLabel =
            typeof marker.label === "string" && marker.label.trim()
              ? marker.label.trim()
              : isActive
                ? "Selected beat"
                : "";

          const showBeatLabel = isActive || !!resolvedBeatLabel;
          const beatPillLeft = clamp(x, 52, Math.max(52, viewportWidthPx - 52));

          return (
            <div
              key={marker.id}
              data-oc-semantic-marker-id={markerId}
              data-oc-semantic-kind={marker.kind}
              data-oc-semantic-marker-active={isActive ? "1" : "0"}
              className="absolute"
              style={{
                left: x,
                top: isActive ? 28 : 30,
                transform: "translateX(-50%)",
              }}
            >
              <div
                style={{
                  width: isActive ? 2 : 1,
                  height: isActive ? 22 : 18,
                  background: isActive ? "rgba(186,230,253,0.95)" : "rgba(34,211,238,0.55)",
                  boxShadow: isActive
                    ? "0 0 14px rgba(34,211,238,0.58)"
                    : "0 0 8px rgba(34,211,238,0.22)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: isActive ? -8 : -6,
                  left: "50%",
                  width: isActive ? 10 : 8,
                  height: isActive ? 10 : 8,
                  transform: "translateX(-50%) rotate(45deg)",
                  borderRadius: 2,
                  background: isActive ? "rgba(186,230,253,0.98)" : "rgba(34,211,238,0.88)",
                  boxShadow: isActive
                    ? "0 0 16px rgba(34,211,238,0.68)"
                    : "0 0 10px rgba(34,211,238,0.26)",
                }}
              />

              {showBeatLabel ? (
                <div
                  className="absolute"
                  data-oc-semantic-label-id={markerId}
                  data-oc-semantic-kind={marker.kind}
                  data-oc-semantic-active={isActive ? "1" : "0"}
                  data-oc-semantic-active-weight={isActive ? "1" : "0"}
                  style={{
                    left: beatPillLeft - x,
                    top: -24,
                    transform: "translateX(-50%)",
                    maxWidth: isActive ? 148 : 120,
                    padding: isActive ? "3px 10px" : "2px 8px",
                    borderRadius: 999,
                    border: isActive
                      ? "1px solid rgba(186,230,253,0.92)"
                      : "1px solid rgba(255,255,255,0.12)",
                    background: isActive
                      ? "rgba(8,145,178,0.72)"
                      : "rgba(34,211,238,0.16)",
                    backdropFilter: "blur(6px)",
                    WebkitBackdropFilter: "blur(6px)",
                    color: isActive ? "rgba(240,253,250,0.99)" : "rgba(255,255,255,0.9)",
                    fontSize: isActive ? 11 : 10,
                    fontWeight: isActive ? 800 : 600,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxShadow: isActive
                      ? "0 0 16px rgba(34,211,238,0.42), 0 4px 12px rgba(0,0,0,0.16)"
                      : "0 4px 12px rgba(0,0,0,0.16)",
                  }}
                >
                  {resolvedBeatLabel}
                </div>
              ) : null}
            </div>
          );
        }

        const emphasis = isActive ? "high" : (marker.emphasis ?? "normal");
        const pillOpacity = emphasis === "high" ? 0.2 : emphasis === "low" ? 0.1 : 0.14;
        const textOpacity = emphasis === "high" ? 0.96 : emphasis === "low" ? 0.74 : 0.86;
        const accentColor = normalizeHexColor(marker.accentColor);
        const inactiveStem = accentColor
          ? `linear-gradient(to bottom, ${hexToRgba(accentColor, 0.14)}, ${hexToRgba(accentColor, 0.42)}, ${hexToRgba(accentColor, 0.12)})`
          : "linear-gradient(to bottom, rgba(196,181,253,0.14), rgba(196,181,253,0.34), rgba(196,181,253,0.12))";
        const activeStem = accentColor
          ? `linear-gradient(to bottom, ${hexToRgba(accentColor, 0.45)}, ${hexToRgba(accentColor, 0.9)}, ${hexToRgba(accentColor, 0.24)})`
          : "linear-gradient(to bottom, rgba(186,230,253,0.45), rgba(103,232,249,0.88), rgba(186,230,253,0.24))";
        const inactivePillBackground = accentColor
          ? hexToRgba(accentColor, pillOpacity)
          : `rgba(196,181,253,${pillOpacity})`;
        const activePillBackground = accentColor
          ? hexToRgba(accentColor, 0.22)
          : "rgba(14,116,144,0.72)";
        const inactivePillBorder = accentColor
          ? `1px solid ${hexToRgba(accentColor, 0.42)}`
          : "1px solid rgba(255,255,255,0.12)";
        const activePillBorder = accentColor
          ? `1px solid ${hexToRgba(accentColor, 0.88)}`
          : "1px solid rgba(186,230,253,0.92)";
        const inactiveTextColor = accentColor
          ? hexToRgba(accentColor, Math.max(0.72, textOpacity))
          : `rgba(255,255,255,${textOpacity})`;
        const activeTextColor = accentColor
          ? hexToRgba(accentColor, 0.98)
          : "rgba(240,253,250,0.99)";
        const activeGlow = accentColor
          ? `0 0 18px ${hexToRgba(accentColor, 0.46)}`
          : "0 0 18px rgba(34,211,238,0.5)";
        const inactiveGlow = accentColor
          ? `0 0 10px ${hexToRgba(accentColor, 0.18)}`
          : "0 0 10px rgba(196,181,253,0.14)";

        // Product bug fix: active marker label remains visible even for unlabeled markers.
        const resolvedLabel =
          typeof marker.label === "string" && marker.label.trim()
            ? marker.label.trim()
            : isActive
              ? "Selected marker"
              : "";
        const resolvedNote = typeof marker.note === "string" && marker.note.trim() ? marker.note.trim() : "";
        const hoverCardTitle = formatMarkerOriginLabel(marker.origin);
        const tickerText = resolvedNote || resolvedLabel;
        const showTicker = !!tickerText && (isHovered || isActive) && tickerText.length > 32;

        const showLabel = isActive || isHovered || (!!resolvedLabel && eventLabelVisibility.has(markerId));

        const pillLeft = clamp(x, 52, Math.max(52, viewportWidthPx - 52));
        const hoverCardLeft = clamp(pillLeft, 100, Math.max(100, viewportWidthPx - 100));

        return (
          <div
            key={marker.id}
            data-oc-semantic-marker-id={markerId}
            data-oc-semantic-kind={marker.kind}
            data-oc-semantic-marker-active={isActive ? "1" : "0"}
          >
            <div
              className="absolute"
              style={{
                left: x,
                top: 0,
                width: 18,
                height: RULER_HEIGHT_PX + 10,
                transform: "translateX(-50%)",
                pointerEvents: "auto",
              }}
              onPointerEnter={() => setHoveredMarkerId(markerId)}
              onPointerLeave={() => setHoveredMarkerId((current) => (current === markerId ? null : current))}
            />
            <div
              className="absolute"
              style={{
                left: x,
                top: 0,
                height: RULER_HEIGHT_PX,
                width: isActive ? 2 : 1,
                transform: "translateX(-50%)",
                background: isActive ? activeStem : inactiveStem,
                boxShadow: isActive ? activeGlow : inactiveGlow,
              }}
            />
            {isHovered && resolvedNote ? (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: hoverCardLeft,
                  top: -78,
                  width: 220,
                  transform: "translateX(-50%)",
                  zIndex: 80,
                }}
              >
                <div
                  className="rounded-2xl border px-3 py-2"
                  style={{
                    borderColor: accentColor ? hexToRgba(accentColor, 0.5) : "rgba(255,255,255,0.18)",
                    background: "rgba(5, 10, 18, 0.94)",
                    boxShadow: `${activeGlow}, 0 16px 36px rgba(0,0,0,0.34)`,
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                  }}
                >
                  <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-white/68">{hoverCardTitle}</div>
                  <div className="mt-1 text-[12px] font-semibold leading-5 text-white/96">{resolvedNote}</div>
                  <div className="mt-1 text-[10px] leading-4 text-white/58">
                    <div>{resolvedLabel || "Untitled marker"}</div>
                    {marker.frameIndex !== null && marker.frameIndex !== undefined ? <div>Frame {marker.frameIndex}</div> : null}
                  </div>
                </div>
              </div>
            ) : null}
            {showLabel ? (
              <div
                className="absolute"
                data-oc-semantic-label-id={markerId}
                data-oc-semantic-kind={marker.kind}
                data-oc-semantic-active={isActive ? "1" : "0"}
                data-oc-semantic-active-weight={isActive ? "1" : "0"}
                onPointerEnter={() => setHoveredMarkerId(markerId)}
                onPointerLeave={() => setHoveredMarkerId((current) => (current === markerId ? null : current))}
                style={{
                  left: pillLeft,
                  top: isActive ? 4 : 6,
                  transform: "translateX(-50%)",
                  maxWidth: isHovered || isActive ? 176 : 120,
                  padding: isActive ? "3px 10px" : "2px 8px",
                  borderRadius: 999,
                  border: isActive ? activePillBorder : inactivePillBorder,
                  background: isActive ? activePillBackground : inactivePillBackground,
                  backdropFilter: "blur(6px)",
                  WebkitBackdropFilter: "blur(6px)",
                  color: isActive ? activeTextColor : inactiveTextColor,
                  fontSize: isActive ? 11 : 10,
                  fontWeight: isActive ? 800 : 600,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  boxShadow: isActive
                    ? `${activeGlow}, 0 4px 12px rgba(0,0,0,0.18)`
                    : "0 4px 12px rgba(0,0,0,0.16)",
                  pointerEvents: "auto",
                }}
              >
                {showTicker ? (
                  <span
                    className="relative block overflow-hidden whitespace-nowrap"
                    style={{ width: isActive ? 156 : 136, maskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)" }}
                  >
                    <span
                      className="inline-flex min-w-max items-center gap-6 whitespace-nowrap"
                      style={{ animation: "ruler-semantic-marker-ticker 9s linear infinite" }}
                    >
                      <span>{tickerText}</span>
                      <span aria-hidden="true">{tickerText}</span>
                    </span>
                  </span>
                ) : (
                  <span className="block max-w-[156px] truncate">{isHovered && resolvedNote ? resolvedNote : resolvedLabel}</span>
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
